/**
 * Morning Boot on the shared record (roadmap S-16, spec
 * docs/9-exploration/app-layer/SPEC-individual-apps.md §2.5).
 *
 * The five steps and their words are the Morning Boot package's
 * (`@morning-boot`, basic mode). What the package kept in the browser is kept
 * on canonical objects instead:
 *   - purge       → the item is archived (exit_status)
 *   - tags        → A / T / G are tags on the item
 *   - top three   → a personal Stack Ranking session (personalRanking.ts)
 *   - timebox     → a commitment due today, owned by the person, linked to the item
 *   - focus       → read back from those, so it survives a reload
 */

import type { BootTag } from '@priorities-ai/morning-boot';
import type { Item } from '@/types/prioritization/lifecycle';
import type { Commitment } from '@/types/prioritization/commitment';

export const WHATS_NEXT_APP_ID = 'whats-next';

/** The tag each Morning Boot mark writes on the item. */
export const MORNING_BOOT_TAGS: Record<BootTag, string> = {
  A: 'avoiding',
  T: 'time-sensitive',
  G: 'north star',
};

const BOOT_TAG_ORDER: BootTag[] = ['A', 'T', 'G'];

/** Items still on the person's plate: not archived, not set aside. */
export function plateItems(items: readonly Item[]): Item[] {
  return items.filter((item) => (item.exit_status ?? 'active') === 'active');
}

/** The Morning Boot marks on an item, in A, T, G order. */
export function bootTagsOf(item: Pick<Item, 'tags'>): BootTag[] {
  const tags = new Set(item.tags ?? []);
  return BOOT_TAG_ORDER.filter((tag) => tags.has(MORNING_BOOT_TAGS[tag]));
}

/** The item's tags with one Morning Boot mark turned on or off. */
export function withBootTag(tags: readonly string[], tag: BootTag, on: boolean): string[] {
  const word = MORNING_BOOT_TAGS[tag];
  const rest = tags.filter((t) => t !== word);
  return on ? [...rest, word] : rest;
}

/** What today's ranking is over: the marked items, or every item when none is marked. */
export function rankingCandidates(items: readonly Item[]): Item[] {
  const plate = plateItems(items);
  const marked = plate.filter((item) => bootTagsOf(item).length > 0);
  return marked.length > 0 ? marked : plate;
}

/** The North Star step: the highest-ranked item marked G, else the top of the ranking. */
export function northStarOf(rankedIds: readonly string[], items: readonly Item[]): Item | undefined {
  const byId = new Map(items.map((item) => [item.id, item]));
  const ranked = rankedIds.map((id) => byId.get(id)).filter((item): item is Item => Boolean(item));
  return ranked.find((item) => bootTagsOf(item).includes('G')) ?? ranked[0];
}

/** Midnight at the start of the day `now` falls in, local time. */
export function startOfDay(now: Date = new Date()): Date {
  const day = new Date(now);
  day.setHours(0, 0, 0, 0);
  return day;
}

/** The local calendar date as YYYY-MM-DD (a commitment's due date). */
export function localIsoDate(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export const TIMEBOX_MINUTES = 30;
const TIMEBOX_TITLE_PREFIX = 'North Star timebox: ';

export function timeboxTitle(itemTitle: string): string {
  return `${TIMEBOX_TITLE_PREFIX}${itemTitle}`;
}

export function timeboxDescription(start: Date): string {
  const at = start.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${TIMEBOX_MINUTES} minutes at ${at}, set in Morning Boot.`;
}

/** Today's timebox among the commitments: owned by the person, due today, made by Morning Boot. */
export function todaysTimebox(
  commitments: readonly Commitment[],
  userId: string,
  now: Date = new Date(),
): Commitment | undefined {
  const today = localIsoDate(now);
  return commitments.find(
    (c) =>
      c.ownerId === userId &&
      c.dueDate === today &&
      c.status === 'active' &&
      c.title.startsWith(TIMEBOX_TITLE_PREFIX),
  );
}
